// Pestaña «Equipo» de la ficha de Tiendas: todo el equipo del padrón, haya vendido o no.
import { useState } from 'react';
import { Skeleton } from '@/kit';
import { cn } from '@/lib/cn';
import { DataTable, type DataTableColumn } from '@/shared/patterns/DataTable';
import { DetailCard } from '@/shared/patterns/DetailCard';
import { NoSourceBlock } from '@/shared/patterns/NoSource';
import { reportPeriodLabel, type ReportPeriod } from '@/shared/patterns/PeriodBand';
import {
    DEFAULT_TEAM_SORT,
    sortTeamRows,
    teamMemberProgress,
    teamRanks,
    type TeamMemberRow,
    type TeamSort,
    type TeamSortKey,
} from '../lib/storeDetail';
import { money } from '../lib/tiendasFormat';
import { AdminChip, GoalBar, RankMedal } from './StoreBadges';

const STATUS_LABEL: Record<string, { label: string; className: string }> = {
    ACTIVE: { label: 'Activo', className: 'bg-success-soft text-success' },
    SUSPENDED: { label: 'Suspendido', className: 'bg-warning-soft text-warning' },
    INACTIVE: { label: 'Inactivo', className: 'bg-muted text-muted-foreground' },
};

export interface StoreTeamState {
    members: TeamMemberRow[] | undefined;
    isError: boolean;
}

export function StoreTeamTab({
    team,
    period,
    onOpenSeller,
}: {
    team: StoreTeamState;
    period: ReportPeriod;
    onOpenSeller: (sellerId: string) => void;
}) {
    const [sort, setSort] = useState<TeamSort>(DEFAULT_TEAM_SORT);

    if (team.isError) {
        return (
            <div className="p-3">
                <NoSourceBlock>No se pudo leer el padrón de vendedores de la tienda.</NoSourceBlock>
            </div>
        );
    }
    if (team.members === undefined) {
        return (
            <div className="p-3">
                <Skeleton lines={4} />
            </div>
        );
    }

    const members = team.members;
    const ranks = teamRanks(members);
    const rows = sortTeamRows(members, sort);
    const isInactive = (row: TeamMemberRow) => row.status !== null && row.status !== 'ACTIVE';

    const columns: DataTableColumn<TeamMemberRow, TeamSortKey>[] = [
        {
            key: 'shortCode',
            label: 'Clave',
            width: '64px',
            sortKey: 'shortCode',
            cellClassName: () => 'font-mono text-[10.5px] font-semibold',
            cell: (row) => row.shortCode,
        },
        {
            key: 'name',
            label: 'Vendedor',
            sortKey: 'name',
            cellTitle: (row) => row.name,
            cell: (row) => (
                <span className="flex min-w-0 items-center gap-1">
                    <RankMedal rank={ranks.get(row.sellerId)} />
                    <span className="truncate font-semibold">{row.name}</span>
                </span>
            ),
        },
        {
            key: 'role',
            label: 'Rol',
            width: '50px',
            sortKey: 'role',
            cellOverflow: 'nowrap',
            cell: (row) =>
                row.isAdmin ? <AdminChip /> : <span className="text-muted-foreground">—</span>,
        },
        {
            key: 'status',
            label: 'Estado',
            width: '74px',
            sortKey: 'status',
            cellOverflow: 'nowrap',
            cell: (row) => {
                const status = row.status === null ? null : STATUS_LABEL[row.status];
                if (!status) return <span className="text-muted-foreground">—</span>;
                return (
                    <span
                        className={cn(
                            'rounded px-1.5 py-px text-[9px] font-bold uppercase tracking-[0.04em]',
                            status.className,
                        )}
                    >
                        {status.label}
                    </span>
                );
            },
        },
        {
            key: 'placed',
            label: 'Colocación',
            width: '90px',
            numeric: true,
            sortKey: 'placed',
            cellClassName: () => 'font-bold',
            cell: (row) => money(row.placedCents),
        },
        {
            key: 'sales',
            label: 'Ventas',
            width: '52px',
            numeric: true,
            sortKey: 'sales',
            cell: (row) => row.salesCount ?? '—',
        },
        {
            key: 'goal',
            label: '% Cumpl. meta',
            width: '70px',
            sortKey: 'goal',
            cellOverflow: 'wrap',
            cell: (row) => <GoalBar progress={teamMemberProgress(row)} />,
        },
        {
            key: 'npi',
            label: 'NPI M30',
            width: '56px',
            numeric: true,
            note: 'El NPI por vendedor todavía no tiene fuente fuera del corte de comisiones',
            cellClassName: () => 'text-muted-foreground',
            cell: () => '—',
        },
        {
            key: 'commission',
            label: 'Comisión',
            width: '70px',
            numeric: true,
            note: 'La comisión del periodo todavía no tiene fuente en esta pantalla',
            cellClassName: () => 'text-muted-foreground',
            cell: () => '—',
        },
    ];

    return (
        <div className="p-3">
            <DetailCard
                title={`Vendedores de la tienda · ${String(members.length)}`}
                meta={`${reportPeriodLabel(period)} · orden default: colocación desc`}
                flush
            >
                <DataTable
                    columns={columns}
                    rows={rows}
                    rowKey={(row) => row.sellerId}
                    sort={sort}
                    onSortToggle={(key) => {
                        setSort((current) =>
                            current.key === key
                                ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
                                : {
                                      key,
                                      direction:
                                          key === 'shortCode' || key === 'name' ? 'asc' : 'desc',
                                  },
                        );
                    }}
                    minWidth="660px"
                    caption="Vendedores de la tienda con rol, estado, colocación, ventas y meta."
                    rowClassName={(row) => (isInactive(row) ? 'opacity-55' : undefined)}
                    onRowClick={(row) => {
                        onOpenSeller(row.sellerId);
                    }}
                    empty={
                        <span className="italic">La tienda no tiene vendedores en el padrón.</span>
                    }
                />
            </DetailCard>
        </div>
    );
}
